import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AddCandidateForm } from '@/components/candidates/AddCandidateForm';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCreateJobApplication } from '@/hooks/useCreateJobApplication';
import type { NewCandidateFormData } from '@/components/candidates/types';
import { CandidateSearchSelect } from '@/components/candidates/CandidateSearchSelect';
import { toast } from 'sonner';

interface AddApplicantDialogProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const existingCandidateFormSchema = z.object({
  candidateId: z.string().min(1, "Please select a candidate."),
  candidateName: z.string().optional(), // To store name for display, not for submission to hook
  notes: z.string().optional(),
});
type ExistingCandidateFormData = z.infer<typeof existingCandidateFormSchema>;

export function AddApplicantDialog({ jobId, isOpen, onClose, onSuccess }: AddApplicantDialogProps) {
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { createJobApplication } = useCreateJobApplication();
  
  const newCandidateNotesForm = useForm({
    defaultValues: {
      notes: '',
    },
  });

  const existingCandidateForm = useForm<ExistingCandidateFormData>({
    resolver: zodResolver(existingCandidateFormSchema),
    defaultValues: {
      candidateId: '',
      candidateName: '',
      notes: '',
    },
  });
  
  const handleSubmitNewCandidate = async (candidateData: NewCandidateFormData) => {
    setIsSubmitting(true);
    try {
      const notes = newCandidateNotesForm.getValues().notes;
      await createJobApplication({
        jobId,
        candidateData,
        notes,
      });
      toast.success("New candidate and application added successfully");
      newCandidateNotesForm.reset();
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to add application: " + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitExistingCandidate = async (data: ExistingCandidateFormData) => {
    setIsSubmitting(true);
    try {
      await createJobApplication({
        jobId,
        candidateId: data.candidateId,
        notes: data.notes,
      });
      toast.success("Application for existing candidate added successfully");
      existingCandidateForm.reset();
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to add application: " + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Reset forms when dialog is closed or tab changes
  useEffect(() => {
    if (!isOpen) {
      newCandidateNotesForm.reset();
      existingCandidateForm.reset();
      setActiveTab('new'); // Reset to default tab
    }
  }, [isOpen, newCandidateNotesForm, existingCandidateForm]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Applicant</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value as 'new' | 'existing');
            // Optionally reset forms on tab change if desired
            // newCandidateNotesForm.reset(); 
            // existingCandidateForm.reset();
        }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new">New Candidate</TabsTrigger>
            <TabsTrigger value="existing">Existing Candidate</TabsTrigger> 
          </TabsList>
          
          <TabsContent value="new" className="mt-4">
            <div className="space-y-6">
              <AddCandidateForm 
                onSubmit={handleSubmitNewCandidate} 
                isSubmitting={isSubmitting} 
                onCancel={onClose}
              />
              
              <div className="border-t pt-4 mt-4">
                <Form {...newCandidateNotesForm}>
                  <form> {/* No onSubmit needed here as it's part of the AddCandidateForm submission flow */}
                    <FormField
                      control={newCandidateNotesForm.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Application Notes (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Add any notes about this application..." 
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="existing" className="mt-4">
            <Form {...existingCandidateForm}>
              <form onSubmit={existingCandidateForm.handleSubmit(handleSubmitExistingCandidate)} className="space-y-6">
                <FormField
                  control={existingCandidateForm.control}
                  name="candidateId"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Select Candidate</FormLabel>
                      <CandidateSearchSelect
                        selectedCandidateId={field.value}
                        onSelectCandidate={(id, name) => {
                          field.onChange(id);
                          existingCandidateForm.setValue("candidateName", name || '');
                          if (id) { // Clear error manually if a selection is made
                            existingCandidateForm.clearErrors("candidateId");
                          }
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={existingCandidateForm.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Application Notes (Optional)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Add any notes about this application..." 
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <DialogFooter className="pt-4">
                  <DialogClose asChild>
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                  </DialogClose>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Adding Application...' : 'Add Application'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
